
const INTERNATIONAL_PHONE_FORMATS = [
  {countryCode: '507', localLengths: [7, 8]},
  {countryCode: '593', localLengths: [9]},
  {countryCode: '506', localLengths: [8]},
  {countryCode: '502', localLengths: [8]},
  {countryCode: '503', localLengths: [8]},
  {countryCode: '504', localLengths: [8]},
  {countryCode: '505', localLengths: [8]},
  {countryCode: '52', localLengths: [10]},
  {countryCode: '57', localLengths: [10]},
  {countryCode: '58', localLengths: [10]},
  {countryCode: '54', localLengths: [10]},
  {countryCode: '51', localLengths: [9]},
  {countryCode: '56', localLengths: [9]},
]

const formatInternationalLocalNumber = (digits: string): string => {
  if (digits.length === 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  if (digits.length === 8) return `${digits.slice(0, 4)}-${digits.slice(4)}`
  if (digits.length === 9) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
  if (digits.length === 10) return `${digits.slice(0, 3)} ${digits.slice(3, 6)}-${digits.slice(6)}`
  if (digits.length > 4) return `${digits.slice(0, 4)}-${digits.slice(4)}`
  return digits
}

const detectInternationalPhone = (digits: string) =>
  INTERNATIONAL_PHONE_FORMATS.find(({countryCode, localLengths}) => {
    if (!digits.startsWith(countryCode)) return false
    return localLengths.includes(digits.length - countryCode.length)
  })

export const formatPhoneDisplay = (
  val?: string | null,
  countryCode: string = '1',
  includePrefix: boolean = true
): string => {
  if (!val) return ''
  let digits = val.replace(/\D/g, '')

  let formatted = digits

  if (countryCode === '1') {
    const internationalPhone = detectInternationalPhone(digits)

    if (internationalPhone) {
      const localDigits = digits.slice(internationalPhone.countryCode.length)
      const formattedLocal = formatInternationalLocalNumber(localDigits)
      return includePrefix ? `+${internationalPhone.countryCode} ${formattedLocal}` : formattedLocal
    }
  }

  // If it's US/Canada
  if (countryCode === '1') {
    if (digits.length === 11 && digits.startsWith('1')) {
      digits = digits.slice(1)
    }
    if (digits.startsWith('1')) {
      digits = digits.slice(1)
    }
    digits = digits.slice(0, 10)
    const part1 = digits.slice(0, 3)
    const part2 = digits.slice(3, 6)
    const part3 = digits.slice(6, 10)

    if (digits.length === 0) formatted = ''
    else if (digits.length <= 3) formatted = `(${part1}`
    else if (digits.length <= 6) formatted = `(${part1}) ${part2}`
    else formatted = `(${part1}) ${part2}-${part3}`
  } else {
    formatted = formatInternationalLocalNumber(digits)
  }

  return includePrefix ? `+${countryCode} ${formatted}` : formatted
}

/**
 * Checks if a string looks like a phone number
 * @param text The text to check
 * @returns true if it looks like a phone number
 */
const looksLikePhoneNumber = (text: string): boolean => {
  // Remove all non-digit characters to count digits
  const digitsOnly = text.replace(/\D/g, '')

  // Phone numbers typically have 10-11 digits
  const hasReasonableDigitCount = digitsOnly.length >= 10 && digitsOnly.length <= 11

  if (!hasReasonableDigitCount) {
    return false
  }

  // Check for common phone formatting characters: (, ), -, space, or +
  const hasPhoneFormatting = /[()\-\s+]/.test(text)

  // It's a phone number if:
  // 1. Starts with +1
  // 2. Has phone formatting and 10-11 digits
  // 3. Is only digits with 10-11 characters (like "6092097394" or "16092097394")
  return text.trim().startsWith('+1') || hasPhoneFormatting || digitsOnly === text.trim()
}

/**
 * Removes the +1 prefix OR leading 1 from a phone number string
 * Only processes if the text looks like a phone number
 * @param text The text to process
 * @returns The text with +1 or leading 1 removed if it's a phone number
 */
export const removePhonePlusOne = (text: string): string => {
  let result = text.trim()

  // Only process if it looks like a phone number
  if (!looksLikePhoneNumber(result)) {
    return result
  }

  // Remove +1 prefix if present (handles "+1 ", "+1", etc.)
  if (result.startsWith('+1')) {
    result = result.substring(2).trim()
  }
  // Otherwise, remove leading 1 if present (handles "1 ", "1(", "1-", "16092097394", etc.)
  else if (result.startsWith('1')) {
    // Check if it's all digits (like "16092097394")
    const digitsOnly = result.replace(/\D/g, '')
    if (digitsOnly === result && digitsOnly.length === 11) {
      // It's a string of 11 digits starting with 1, remove the leading 1
      result = result.substring(1)
    } else {
      // It has formatting (like "1 (609) 209-7394"), remove the leading 1 and trim
      result = result.substring(1).trim()
    }
  }

  return result
}
